import { useEffect, useState } from 'react';
import { Link, Navigate, useParams, useSearchParams } from 'react-router-dom';
import { useEventManager } from '../hooks/useEventManager';
import { getEventTicketOrderStatus } from '../services/event-payments';

type ConfirmationState = {
  status: 'loading' | 'paid' | 'processing' | 'refunded' | 'failed' | 'canceled' | 'pending';
  message: string;
};

export default function EventTicketThankYou() {
  const { slug } = useParams();
  const [searchParams] = useSearchParams();
  const { getEventBySlug, isLoading } = useEventManager();
  const event = slug ? getEventBySlug(slug) : undefined;
  const orderId = searchParams.get('order_id');
  const paymentIntentId = searchParams.get('payment_intent');
  const [confirmation, setConfirmation] = useState<ConfirmationState>({
    status: 'loading',
    message: 'Confirming your ticket payment now.',
  });

  useEffect(() => {
    let isActive = true;

    async function loadOrderStatus() {
      if (!orderId && !paymentIntentId) {
        if (isActive) {
          setConfirmation({
            status: 'pending',
            message: 'Your ticket request was received and is waiting for payment confirmation.',
          });
        }
        return;
      }

      try {
        const response = await getEventTicketOrderStatus({
          orderId: orderId || undefined,
          paymentIntentId: paymentIntentId || undefined,
        });

        if (!isActive) {
          return;
        }

        const order = response.order;
        if (order.payment_status === 'paid') {
          setConfirmation({
            status: 'paid',
            message: 'Payment received. Victoria\'s Place will follow up with your ticket and event details by email.',
          });
          return;
        }

        if (order.payment_status === 'processing') {
          setConfirmation({
            status: 'processing',
            message: 'Payment is processing. Victoria\'s Place will confirm your ticket as soon as Stripe completes the charge.',
          });
          return;
        }

        if (order.payment_status === 'refunded') {
          setConfirmation({
            status: 'refunded',
            message: 'This ticket order was refunded. Victoria\'s Place can help if you have any follow-up questions.',
          });
          return;
        }

        if (order.payment_status === 'failed') {
          setConfirmation({
            status: 'failed',
            message: order.last_error || 'Payment failed. Please try again or contact Victoria\'s Place for help.',
          });
          return;
        }

        if (order.payment_status === 'canceled') {
          setConfirmation({
            status: 'canceled',
            message: 'Payment was canceled before the ticket was completed.',
          });
          return;
        }

        setConfirmation({
          status: 'pending',
          message: 'Your ticket request is recorded and awaiting payment confirmation.',
        });
      } catch (error) {
        if (isActive) {
          setConfirmation({
            status: 'pending',
            message:
              error instanceof Error
                ? error.message
                : 'Your payment was submitted, but the ticket status could not be confirmed yet.',
          });
        }
      }
    }

    void loadOrderStatus();

    return () => {
      isActive = false;
    };
  }, [orderId, paymentIntentId]);

  if (isLoading) {
    return (
      <div className="page-shell confirmation-page">
        <section className="confirmation-card">
          <p className="home-section-label">Ticket confirmation</p>
          <h1>Loading your event details...</h1>
        </section>
      </div>
    );
  }

  if (!slug || !event) {
    return <Navigate to="/events" replace />;
  }

  return (
    <div className="page-shell confirmation-page">
      <section className="confirmation-card">
        <p className="home-section-label">Ticket confirmation</p>
        <h1>{event.title}</h1>
        <p className="confirmation-copy">{confirmation.message}</p>

        <div className="confirmation-actions">
          <Link to={`/events/${event.slug}`} className="button-primary tap-target">
            Back to event
          </Link>
          <Link to="/events" className="button-secondary tap-target">
            Browse events
          </Link>
        </div>
      </section>
    </div>
  );
}
